<template>
  <div>
    <v-dialog
      v-model="internalValue"
      max-width="1000px"
      transition="dialog-bottom-transition"
      scrollable
    >
      <v-card class="ds-dialog">
        <v-card-title class="ds-dialog-header">
          <v-icon color="white" class="mr-2" style="position: relative; z-index: 1"
            >mdi-account-child-circle</v-icon
          >
          <span class="ds-dialog-title">
            Dependentes<span v-if="item && item.name"> de {{ item.name }}</span>
          </span>
          <v-spacer />
          <v-btn
            small
            color="white"
            class="mr-2 add-dependent-btn"
            depressed
            style="position: relative; z-index: 1"
            data-tour="dep-add"
            @click="openAddDependent"
          >
            <v-icon left small>mdi-plus</v-icon>
            Adicionar dependente
          </v-btn>
          <v-btn
            icon
            class="close-btn"
            color="white"
            aria-label="Ver tour de dependentes"
            @click="startTour"
          >
            <v-icon>mdi-help-circle-outline</v-icon>
          </v-btn>
          <v-btn icon class="close-btn" color="white" @click="closeDialog">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-card-title>

        <v-card-text class="ds-dialog-body">
          <section class="ds-form-section">
            <div class="ds-form-section__head">
              <div class="ds-form-section__title">
                <v-icon small color="primary" class="mr-2">mdi-account-child</v-icon>
                <span>Dependentes</span>
              </div>
              <span v-if="!loading" class="ds-form-section__aside">{{ dependents.length }}</span>
            </div>

            <!-- Carregando -->
            <v-row v-if="loading" justify="center" class="my-10">
              <v-progress-circular
                indeterminate
                color="primary"
                size="50"
              ></v-progress-circular>
            </v-row>

            <!-- Lista de dependentes -->
            <v-row v-else-if="dependents.length" dense>
              <v-col
                v-for="(dep, di) in dependents"
                :key="dep.id"
                cols="12"
                sm="6"
                md="4"
                :data-tour="di === 0 ? 'dep-list' : null"
              >
                <v-card outlined class="dependent-card" hover>
                  <v-card-title class="pa-3">
                    <v-avatar size="50" class="mr-3">
                      <v-img :src="dep.imageUrl" />
                    </v-avatar>
                    <div class="flex-grow-1" style="min-width: 0">
                      <div class="font-weight-medium text-subtitle-1 text-truncate">
                        {{ dep.name }}
                      </div>
                      <div class="text-caption grey--text text-truncate">
                        {{ dep.email || "Sem e-mail" }}
                      </div>
                      <div class="d-flex align-center mt-1" style="gap: 6px">
                        <span v-if="dep.age !== null" class="text-caption primary--text">
                          {{ dep.age }} anos
                        </span>
                        <v-chip
                          x-small
                          :color="dep.status === 1 ? 'success' : 'grey'"
                          text-color="white"
                          label
                        >
                          {{ dep.status === 1 ? "Ativo" : "Inativo" }}
                        </v-chip>
                      </div>
                    </div>
                  </v-card-title>

                  <!-- "Acessar" leva ao prontuário, que é só do médico: a
                       assistente seria devolvida para a home ao clicar. -->
                  <v-card-actions v-if="podeVerProntuario" class="pa-3 pt-0">
                    <v-btn color="primary" text small @click="goToProfile(dep.id)">
                      <v-icon left small>mdi-account-eye</v-icon>
                      Acessar
                    </v-btn>
                  </v-card-actions>
                </v-card>
              </v-col>
            </v-row>

            <!-- Vazio -->
            <div
              v-else
              class="text-center py-6"
            >
              <v-icon size="64" color="grey lighten-1" class="mb-4">
                mdi-account-child-outline
              </v-icon>
              <div class="text-h6 grey--text mb-2">Nenhum dependente vinculado</div>
              <div class="text-body-2 grey--text mb-4">
                Cadastre um dependente para vinculá-lo a este responsável.
              </div>
              <v-btn color="primary" @click="openAddDependent">
                <v-icon left>mdi-plus</v-icon>
                Adicionar dependente
              </v-btn>
            </div>
          </section>
        </v-card-text>
      </v-card>
    </v-dialog>

    <!-- Cadastro de dependente já com o responsável definido -->
    <AddNewUserDialog
      v-model="addDependentOpen"
      :key="'add-dep-' + (item && item.id)"
      target-type="patient"
      :external-guardian-id="guardianId"
      :external-guardian-address="item && item.address ? item.address : null"
      @updatePersonsPage="onDependentAdded"
    />
  </div>
</template>

<script>
import http from "@/services/http";
import { useSnackbar } from "@/composables/useSnackbar";
import { getProfileImage } from "@/utils/getProfileImage";
import AddNewUserDialog from "@/features/patients/add-user/AddNewUserDialog.vue";
import tourDialogMixin from "@/features/tour/tourDialogMixin";
import { calculateAge } from "@/utils/calculateAge.js";

export default {
  name: "DependentsDialog",

  mixins: [tourDialogMixin],

  components: { AddNewUserDialog },

  props: {
    item: { type: Object, required: true },
    value: { type: Boolean, default: false },
  },

  setup() {
    const { success, error, erroDeRequisicao } = useSnackbar();
    return { success, error, erroDeRequisicao };
  },

  data() {
    return {
      dependents: [],
      loading: false,
      addDependentOpen: false,
      _blobUrls: new Set(),
    };
  },

  computed: {
    /** Prontuário (destino do "Acessar") é só do médico — roleId 1. */
    podeVerProntuario() {
      return this.$store.getters["auth/roleId"] === 1;
    },

    tourId() {
      return "dialog-dependentes";
    },
    internalValue: {
      get() {
        return this.value;
      },
      set(val) {
        this.$emit("input", val);
      },
    },
    guardianId() {
      const id = this.item?.id;
      return id ? Number(id) : null;
    },
  },

  watch: {
    value: {
      immediate: true,
      handler(val) {
        if (val) {
          this.fetchDependents();
          this.tourOnOpen(); // tour guiado na 1ª vez que abre
        } else this.revokeBlobs();
      },
    },
  },

  beforeDestroy() {
    this.revokeBlobs();
  },

  methods: {
    calculateAge: (v) => calculateAge(v, { fallback: null }),
    tourReady() {
      return !this.loading;
    },
    tourSteps() {
      return [
        {
          title: "Dependentes",
          description:
            "Dependentes são pacientes vinculados a um responsável (por exemplo, filhos menores). Aqui você vê e gerencia os dependentes desta pessoa.",
        },
        {
          anchor: "dep-add",
          title: "Adicionar dependente",
          description:
            "Cadastra um novo dependente já vinculado a este responsável — o endereço do responsável é reaproveitado, agilizando o cadastro.",
        },
        {
          anchor: "dep-list",
          title: "Dependentes vinculados",
          description:
            "Cada dependente aparece como um card com nome, idade e status. Em “Acessar” você abre o perfil completo dele.",
        },
      ];
    },
    closeDialog() {
      this.$emit("input", false);
    },

    async fetchDependents() {
      if (!this.item?.id) return;
      this.loading = true;
      this.revokeBlobs();
      try {
        const { data } = await http.get(
          `/api/relation/dependents-by-guardian/${this.item.id}`,
        );
        const raw = data?.data || [];
        this.dependents = await Promise.all(
          raw.map(async (dep) => {
            const imageUrl = await getProfileImage(dep.profile_image);
            if (typeof imageUrl === "string" && imageUrl.startsWith("blob:")) {
              this._blobUrls.add(imageUrl);
            }
            return { ...dep, imageUrl, age: this.calculateAge(dep.birth_date) };
          }),
        );
      } catch (err) {
        console.error("Erro ao buscar dependentes:", err);
        this.erroDeRequisicao(err, "Erro ao carregar dependentes.");
        this.dependents = [];
      } finally {
        this.loading = false;
      }
    },

    openAddDependent() {
      if (!this.guardianId) {
        this.error("Responsável não identificado.");
        return;
      }
      this.addDependentOpen = true;
    },

    async onDependentAdded() {
      // Atualiza a lista sem recarregar a página e avisa a tela de origem.
      await this.fetchDependents();
      this.$emit("refresh");
    },

    goToProfile(dependentId) {
      if (!dependentId) return;
      this.$router.push(`/patient-profile-dashboard/${dependentId}`);
    },

    revokeBlobs() {
      if (!this._blobUrls) {
        this._blobUrls = new Set();
        return;
      }
      for (const url of this._blobUrls) {
        try {
          if (url && String(url).startsWith("blob:")) URL.revokeObjectURL(url);
        } catch {}
      }
      this._blobUrls.clear();
    },
  },
};
</script>

<style scoped>
.ds-dialog {
  border-radius: 4px !important;
  overflow: hidden;
  box-shadow: 0 24px 60px rgba(31, 77, 63, 0.18),
    0 4px 12px rgba(0, 0, 0, 0.06) !important;
}
.ds-dialog-header {
  background: linear-gradient(135deg, var(--ds-primary) 0%, var(--ds-primary-deep) 100%);
  color: #fff;
}
.ds-dialog-title {
  font-size: 16px;
  font-weight: 700;
  letter-spacing: 0.3px;
  position: relative;
  z-index: 1;
}
.add-dependent-btn {
  color: var(--ds-primary-deep) !important;
}
.close-btn {
  position: relative;
  z-index: 1;
}
.close-btn:hover {
  background-color: rgba(255, 255, 255, 0.15) !important;
}
.dependent-card {
  transition: all 0.3s ease;
  border-radius: 8px !important;
}
.dependent-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15) !important;
}
</style>
